import { useState, useEffect, useCallback, useRef } from "react";
import { naverLeagueStandings as mockLeagueStandings } from "@/mocks/homeData";

export interface TeamRow {
  rank: number;
  team: string;
  logo: string;
  points: number;
  games: number;
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDiff: number;
}

export interface QualRule {
  cl: number;
  el: number;
  relStart: number;
  clLabel: string;
  elLabel: string;
  totalTeams: number;
}

export type SportType = "축구" | "야구" | "농구" | "배구";

export interface SportConfig {
  leagues: string[];
  defaultLeague: string;
  colLabels: { points: string; games: string; wins: string };
  statSuffix?: string;
}

export const SPORT_LIST: SportType[] = ["축구", "야구", "농구", "배구"];

export const SPORT_CONFIGS: Record<SportType, SportConfig> = {
  축구: {
    leagues: ["EPL", "라리가", "세리에A", "분데스리가", "리그1"],
    defaultLeague: "EPL",
    colLabels: { points: "승점", games: "경기", wins: "승" },
  },
  야구: {
    leagues: ["KBO"],
    defaultLeague: "KBO",
    colLabels: { points: "승률", games: "경기", wins: "승" },
    statSuffix: "승률",
  },
  농구: {
    leagues: ["KBL"],
    defaultLeague: "KBL",
    colLabels: { points: "승률", games: "경기", wins: "승" },
    statSuffix: "승률",
  },
  배구: {
    leagues: ["V리그"],
    defaultLeague: "V리그",
    colLabels: { points: "승점", games: "경기", wins: "승" },
  },
};

export const FALLBACK_RULES: Record<string, QualRule> = {
  EPL: { cl: 4, el: 5, relStart: 18, clLabel: "챔스리그", elLabel: "유로파리그", totalTeams: 20 },
  라리가: { cl: 4, el: 5, relStart: 18, clLabel: "챔스리그", elLabel: "유로파리그", totalTeams: 20 },
  세리에A: { cl: 4, el: 5, relStart: 18, clLabel: "챔스리그", elLabel: "유로파리그", totalTeams: 20 },
  분데스리가: { cl: 4, el: 5, relStart: 17, clLabel: "챔스리그", elLabel: "유로파리그", totalTeams: 18 },
  리그1: { cl: 3, el: 5, relStart: 17, clLabel: "챔스리그", elLabel: "유로파리그", totalTeams: 18 },
  KBO: { cl: 5, el: 0, relStart: 0, clLabel: "플레이오프", elLabel: "", totalTeams: 10 },
  KBL: { cl: 6, el: 0, relStart: 0, clLabel: "플레이오프", elLabel: "", totalTeams: 10 },
  "V리그": { cl: 3, el: 0, relStart: 0, clLabel: "플레이오프", elLabel: "", totalTeams: 7 },
};

export function formatPointValue(points: number, sport: SportType): string {
  if (sport === "야구" || sport === "농구") {
    return (points / 1000).toFixed(3);
  }
  return String(points);
}

export function getTeamBorderColor(rank: number, league: string, rulesData: Record<string, QualRule>): string {
  const rules = rulesData[league] || FALLBACK_RULES[league];
  if (!rules) return "";
  if (rank <= rules.cl) return "border-l-2 border-l-blue-500";
  if (rules.el > rules.cl && rank <= rules.el) return "border-l-2 border-l-orange-500";
  if (rules.relStart > 0 && rank >= rules.relStart) return "border-l-2 border-l-red-500";
  return "";
}

export function getTeamRankColor(rank: number, league: string, rulesData: Record<string, QualRule>): string {
  const rules = rulesData[league] || FALLBACK_RULES[league];
  if (!rules) return "text-foreground-500";
  if (rank <= rules.cl) return "text-blue-500";
  if (rules.el > rules.cl && rank <= rules.el) return "text-orange-500";
  if (rules.relStart > 0 && rank >= rules.relStart) return "text-red-500";
  return "text-foreground-500";
}

export function getLegendItems(league: string, rulesData: Record<string, QualRule>): Array<{ color: string; label: string }> {
  const rules = rulesData[league] || FALLBACK_RULES[league];
  if (!rules) return [];
  const items: Array<{ color: string; label: string }> = [];
  if (rules.cl > 0) {
    items.push({ color: "bg-blue-500", label: `1~${rules.cl}위 ${rules.clLabel}` });
  }
  if (rules.el > rules.cl) {
    items.push({ color: "bg-orange-500", label: `${rules.cl + 1}~${rules.el}위 ${rules.elLabel}` });
  }
  if (rules.relStart > 0) {
    items.push({ color: "bg-red-500", label: `${rules.relStart}~${rules.totalTeams}위 강등` });
  }
  return items;
}

export interface UseSportsStandingsReturn {
  sportTab: SportType;
  setSportTab: (sport: SportType) => void;
  leagueTab: string;
  setLeagueTab: (league: string) => void;
  standingsData: Record<string, TeamRow[]>;
  rulesData: Record<string, QualRule>;
  loadingMap: Record<string, boolean>;
  errorMap: Record<string, string | null>;
  displayRows: TeamRow[];
  isLoading: boolean;
  hasError: string | null;
  useMock: boolean;
  currentSportConfig: SportConfig;
  cacheKey: string;
  handleSportChange: (sport: SportType) => void;
  retryFetch: () => void;
}

export function useSportsStandings(initialSport: SportType = "축구", initialLeague?: string): UseSportsStandingsReturn {
  const [sportTab, setSportTab] = useState<SportType>(initialSport);
  const [leagueTab, setLeagueTab] = useState<string>(initialLeague || SPORT_CONFIGS[initialSport].defaultLeague);
  const [standingsData, setStandingsData] = useState<Record<string, TeamRow[]>>({});
  const [rulesData, setRulesData] = useState<Record<string, QualRule>>({});
  const [loadingMap, setLoadingMap] = useState<Record<string, boolean>>({});
  const [errorMap, setErrorMap] = useState<Record<string, string | null>>({});

  const currentSportConfig = SPORT_CONFIGS[sportTab];
  const cacheKey = `${sportTab}::${leagueTab}`;

  const fetchedRef = useRef<Set<string>>(new Set());

  const fetchStandings = useCallback(async (sport: SportType, league: string) => {
    const key = `${sport}::${league}`;
    if (fetchedRef.current.has(key)) return;
    fetchedRef.current.add(key);

    setLoadingMap((prev) => ({ ...prev, [key]: true }));
    setErrorMap((prev) => ({ ...prev, [key]: null }));

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      const resp = await fetch(
        `${supabaseUrl}/functions/v1/naver-sports-standings?sport=${encodeURIComponent(sport)}&league=${encodeURIComponent(league)}`,
        {
          headers: {
            Authorization: `Bearer ${supabaseKey}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!resp.ok) {
        const errText = await resp.text();
        throw new Error(errText || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      if (data.error) throw new Error(data.error);

      setStandingsData((prev) => ({ ...prev, [key]: data.standings || [] }));
      if (data.qualification_rules) {
        setRulesData((prev) => ({ ...prev, [league]: data.qualification_rules }));
      }
    } catch (e) {
      setErrorMap((prev) => ({
        ...prev,
        [key]: e instanceof Error ? e.message : "데이터를 불러오지 못했습니다",
      }));
    } finally {
      setLoadingMap((prev) => ({ ...prev, [key]: false }));
    }
  }, []);

  useEffect(() => {
    fetchStandings(sportTab, leagueTab);
  }, [sportTab, leagueTab, fetchStandings]);

  const handleSportChange = useCallback((sport: SportType) => {
    setSportTab(sport);
    setLeagueTab(SPORT_CONFIGS[sport].defaultLeague);
  }, []);

  const retryFetch = useCallback(() => {
    fetchedRef.current.delete(cacheKey);
    setErrorMap((prev) => ({ ...prev, [cacheKey]: null }));
    setStandingsData((prev) => {
      const next = { ...prev };
      delete next[cacheKey];
      return next;
    });
    // trigger re-fetch via useEffect by temporarily clearing and re-adding
    fetchStandings(sportTab, leagueTab);
  }, [cacheKey, sportTab, leagueTab, fetchStandings]);

  const isLoading = loadingMap[cacheKey] || false;
  const hasError = errorMap[cacheKey] || null;
  const liveRows = standingsData[cacheKey] || [];
  const useMock = !isLoading && (!!hasError || liveRows.length === 0);
  const mockKey = leagueTab as keyof typeof mockLeagueStandings;
  const mockRows = mockLeagueStandings[mockKey] || [];
  const displayRows: TeamRow[] = useMock ? (mockRows as TeamRow[]) : liveRows;

  return {
    sportTab,
    setSportTab,
    leagueTab,
    setLeagueTab,
    standingsData,
    rulesData,
    loadingMap,
    errorMap,
    displayRows,
    isLoading,
    hasError,
    useMock,
    currentSportConfig,
    cacheKey,
    handleSportChange,
    retryFetch,
  };
}